import { useMemo, useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { History, Eye, Trash2, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DocumentFileSection } from "@/components/documents/DocumentFileSection";
import { DocumentVersionHistory } from "@/components/documents/DocumentVersionHistory";
import { DocumentAccessLog } from "@/components/documents/DocumentAccessLog";
import { useUpdateDocument, useAttachFile, useReplaceVersion } from "@/hooks/useDocuments";
import { getDocumentFormSchema, type DocumentFormSchemaType, DocumentFormInput } from "@/schemas/documentSchema";
import type { Document } from "@/types/document";
import {
  CONFIDENTIALITY_LABELS,
  DISPLAY_TARGET_LABELS,
  DOCUMENT_CATEGORY_LABELS,
} from "./documentLabels";
import {
  CONFIDENTIALITY_LEVELS,
  DOCUMENT_CATEGORIES,
  DISPLAY_TARGETS,
} from "@/types/document";

interface UploadedFile {
  fileId: string;
  fileName: string;
  previewUrl?: string;
}

interface EditDocumentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  document: Document | null;
  onDelete?: (doc: Document) => void;
}

export function EditDocumentSheet({
  open,
  onOpenChange,
  document,
  onDelete,
}: EditDocumentSheetProps) {
  const { i18n } = useLingui();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n object is stable; only locale determines schema output
  const documentSchema = useMemo(() => getDocumentFormSchema(i18n), [i18n.locale]);

  const [uploadedFile, setUploadedFile] = useState<UploadedFile | null>(null);
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [accessLogOpen, setAccessLogOpen] = useState(false);

  const form = useForm<DocumentFormInput, unknown, DocumentFormSchemaType>({
    resolver: zodResolver(documentSchema),
    defaultValues: {
      name: "",
      description: "",
      confidentiality: "public",
      category: "",
      is_published: false,
      display_targets: [],
    },
  });

  useEffect(() => {
    if (document) {
      form.reset({
        name: document.name,
        description: document.description ?? "",
        confidentiality: document.confidentiality,
        category: document.category,
        is_published: document.is_published,
        display_targets: document.display_targets ?? [],
      });
      if (uploadedFile?.previewUrl) {
        URL.revokeObjectURL(uploadedFile.previewUrl);
      }
      setUploadedFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset on document change
  }, [document, form]);

  const updateMutation = useUpdateDocument();
  // Silent: the update toast reports the whole save, file included.
  const attachFileMutation = useAttachFile({ silent: true });
  const replaceVersionMutation = useReplaceVersion({ silent: true });

  const handleClose = () => {
    form.reset();
    if (uploadedFile?.previewUrl) {
      URL.revokeObjectURL(uploadedFile.previewUrl);
    }
    setUploadedFile(null);
    setVersionHistoryOpen(false);
    setAccessLogOpen(false);
    onOpenChange(false);
  };

  const handleFileRemoved = useCallback(() => {
    setUploadedFile(null);
  }, []);

  const onSubmit = async (values: DocumentFormSchemaType) => {
    if (!document) return;

    await updateMutation.mutateAsync({
      // `document` is a persisted record here (guarded above), so `id` is set.
      id: document.id!,
      data: {
        name: values.name,
        description: values.description || undefined,
        confidentiality: values.confidentiality,
        category: values.category,
        is_published: values.is_published,
        display_targets: values.display_targets,
        // A persisted document always carries its optimistic-lock version.
        version: document.version!,
      },
    });

    if (uploadedFile && document.id) {
      if (document.file_id) {
        await replaceVersionMutation.mutateAsync({
          id: document.id,
          data: { file_id: uploadedFile.fileId },
        });
      } else {
        await attachFileMutation.mutateAsync({
          id: document.id,
          data: { file_id: uploadedFile.fileId },
        });
      }
    }

    handleClose();
  };

  const isSaving = updateMutation.isPending || attachFileMutation.isPending || replaceVersionMutation.isPending;

  const existingFile = document?.filename
    ? {
        fileName: document.filename,
        version: document.current_version ?? 1,
        downloadUrl: document.download_url,
      }
    : null;

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent size="md">
          <SheetHeader>
            <SheetTitle>
              <Trans id="documents.edit.title">Edit Document</Trans>
            </SheetTitle>
            <SheetDescription>
              <Trans id="documents.edit.description">
                Update document metadata
              </Trans>
            </SheetDescription>
          </SheetHeader>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-4">
              <div>
                <p className="text-sm font-medium mb-1">
                  <Trans id="documents.fields.version">Version</Trans>
                </p>
                <Badge variant="secondary">
                  v{document?.current_version ?? 1}
                </Badge>
              </div>

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>
                      <Trans id="documents.fields.name">Name</Trans>
                    </FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      <Trans id="documents.fields.description">Description</Trans>
                    </FormLabel>
                    <FormControl>
                      <Textarea {...field} rows={3} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>
                      <Trans id="documents.fields.category">Category</Trans>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {DOCUMENT_CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {i18n._(DOCUMENT_CATEGORY_LABELS[cat.value])}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confidentiality"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>
                      <Trans id="documents.fields.confidentiality">Confidentiality</Trans>
                    </FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CONFIDENTIALITY_LEVELS.map((level) => (
                          <SelectItem key={level.value} value={level.value}>
                            {i18n._(CONFIDENTIALITY_LABELS[level.value])}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="is_published"
                render={({ field }) => (
                  <FormItem className="flex items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>
                        <Trans id="documents.fields.published">Published</Trans>
                      </FormLabel>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="display_targets"
                render={() => (
                  <FormItem>
                    <FormLabel>
                      <Trans id="documents.fields.displayTargets">Display Targets</Trans>
                    </FormLabel>
                    <div className="space-y-2">
                      {DISPLAY_TARGETS.map((target) => (
                        <FormField
                          key={target.value}
                          control={form.control}
                          name="display_targets"
                          render={({ field }) => (
                            <FormItem className="flex items-center space-x-2">
                              <FormControl>
                                <Checkbox
                                  checked={field.value?.includes(target.value)}
                                  onCheckedChange={(checked) => {
                                    const current = field.value ?? [];
                                    if (checked) {
                                      field.onChange([...current, target.value]);
                                    } else {
                                      field.onChange(
                                        current.filter((v: string) => v !== target.value)
                                      );
                                    }
                                  }}
                                />
                              </FormControl>
                              <FormLabel className="font-normal">
                                {i18n._(DISPLAY_TARGET_LABELS[target.value])}
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DocumentFileSection
                uploadedFile={uploadedFile}
                existingFile={existingFile}
                onFileUploaded={setUploadedFile}
                onFileRemoved={handleFileRemoved}
              />

              {/* Versions & Access Logs buttons next to file */}
              {document && (
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setVersionHistoryOpen(true)}
                  >
                    <History className="mr-1 h-3.5 w-3.5" />
                    <Trans id="documents.actions.versions">Versions</Trans>
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAccessLogOpen(true)}
                  >
                    <Eye className="mr-1 h-3.5 w-3.5" />
                    <Trans id="documents.actions.accessLogs">Access Logs</Trans>
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-between pt-4">
                {onDelete && document ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => onDelete(document)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    <Trans id="common.delete">Delete</Trans>
                  </Button>
                ) : (
                  <div />
                )}
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={handleClose}>
                    <Trans id="common.cancel">Cancel</Trans>
                  </Button>
                  <Button type="submit" disabled={isSaving}>
                    {isSaving && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                    )}
                    <Trans id="common.save">Save</Trans>
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      {/* Version History Dialog */}
      <DocumentVersionHistory
        open={versionHistoryOpen}
        onOpenChange={setVersionHistoryOpen}
        document={document}
      />

      {/* Access Log Dialog */}
      <DocumentAccessLog
        open={accessLogOpen}
        onOpenChange={setAccessLogOpen}
        document={document}
      />
    </>
  );
}
